import React from "react";
import { Product, SelectedProduct } from "./types";
import { Order } from "../OrderInfo/types";
import { Search, Plus, Minus, ChevronLeft, ChevronRight } from "lucide-react";
import medicineIcon from "../../../../../assets/images/medicineIcon.png";
import { useAuth } from "../../../../../context/AuthContext";

interface ProductsSectionProps {
  availableProducts: Product[];
  selectedProducts: SelectedProduct[];
  onAddProduct: (product: Product) => void;
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onRemoveProduct: (productId: string) => void;
  loading?: boolean;
  error?: string | null;
  selectedOrder?: Order | null;
  onSearch: (searchTerm: string) => void;
  pagination: {
    currentPage: number;
    itemsPerPage: number;
    totalItemsCount: number;
    totalPages: number;
    nextPage: number | null;
    previousPage: number | null;
  };
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  isDoctorRequired?: boolean;
  doctorName: string | undefined;
  setDoctorName: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const ProductsSection: React.FC<ProductsSectionProps> = ({
  availableProducts,
  selectedProducts,
  onAddProduct,
  onUpdateQuantity,
  onRemoveProduct,
  selectedOrder,
  loading,
  error,
  onSearch,
  pagination,
  onPageChange,
  onItemsPerPageChange,
  isDoctorRequired,
  doctorName,
  setDoctorName,
}) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchTimeout, setSearchTimeout] =
    React.useState<NodeJS.Timeout | null>(null);
  const { user } = useAuth();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    setSearchTimeout(
      setTimeout(() => {
        onSearch(value);
      }, 500)
    );
  };

  React.useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const showOrderProductItems = selectedOrder?.productOrderItems?.length > 0;

  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = parseInt(e.target.value);
    onItemsPerPageChange(value);
  };

  return (
    <div>
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-4 mt-4">
          {showOrderProductItems ? "Ordered Products" : ""}
        </h2>

        {showOrderProductItems ? (
          <>
            <div className="">
              {selectedOrder?.productOrderItems?.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-3 bg-white rounded-lg"
                >
                  {item.productImage && (
                    <img
                      src={item.productImage || medicineIcon}
                      alt={item.productName}
                      width={50}
                      height={50}
                      className="rounded border"
                    />
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-gray-800">
                      {item.productName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {item.productDescription}
                    </p>
                    <p className="text-xs text-gray-500">
                      MRP: ₹{item.productPrice?.product_mrp} | Price: ₹
                      {item.productPrice?.final_selling_price} |
                      {item.productPrice?.selling_discount}% off
                    </p>
                  </div>
                  <div className="bg-gray-100 px-3 py-1 rounded-full text-sm font-medium">
                    {item.productsItemsCount || 1}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div>
              <div className="flex justify-between items-center mb-4 w-full">
                <h2 className="text-lg font-semibold text-gray-800">
                  Products
                </h2>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent bg-cyan-50"
                  />
                </div>
              </div>

              {loading && (
                <div className="text-center py-4">Loading products...</div>
              )}
              {error && (
                <div className="text-red-500 text-center py-4">{error}</div>
              )}

              <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-500 mb-2 bg-cyan-50 p-2">
                <div
                  className={`${
                    user?.role == "clinic" ? "col-span-3" : "col-span-3"
                  }`}
                >
                  Product Name
                </div>
                <div
                  className={`${
                    user?.role == "clinic" ? "col-span-3" : "col-span-3"
                  }`}
                >
                  Product Salt
                </div>
                {user?.role == "admin" && (
                  <div className="col-span-2">Price</div>
                )}
                <div
                  className={`${
                    user?.role == "clinic" ? "col-span-1" : "col-span-2"
                  }`}
                >
                  Quantity
                </div>
                <div
                  className={`${
                    user?.role == "clinic" ? "col-span-2" : "col-span-2"
                  }`}
                >
                  Actions
                </div>
              </div>

              <div
                className="space-y-2 max-h-[200px] overflow-y-auto"
                style={{
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "thin",
                }}
              >
                {availableProducts.map((product) => (
                  <div
                    key={product.compositeId}
                    className="grid grid-cols-12 gap-4 items-center p-3 rounded-lg"
                  >
                    <div
                      className={`${
                        user?.role == "clinic" ? "col-span-3" : "col-span-3"
                      } flex items-center gap-3`}
                    >
                      <img
                        src={product.product_image || medicineIcon}
                        alt={product.product_name}
                        className="w-10 h-10 object-cover rounded"
                      />
                      <div>
                        <div className="font-medium text-gray-800">
                          {product.product_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {product.manufacturer_name}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`${
                        user?.role == "clinic" ? "col-span-3" : "col-span-3"
                      } flex items-center gap-3`}
                    >
                      <div>
                        <div className="font-medium text-gray-800">
                          {product?.salt?.split(",").map((salt, index) => (
                            <div key={index}>{salt.trim()}</div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div
                      className={`${
                        user?.role == "clinic" ? "col-span-2" : "col-span-2"
                      }`}
                    >
                      <div className="font-medium">
                        ₹ {product?.finalSP?.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-500">
                        Expires:{" "}
                        {product.expiry_date
                          ? (() => {
                              const date = new Date(product.expiry_date);
                              const month = String(date.getMonth() + 1).padStart(
                                2,
                                "0"
                              ); // Months are 0-indexed
                              const year = String(date.getFullYear()).slice(-2); // Get last 2 digits of year
                              return `${month}/${year}`;
                            })()
                          : "N/A"}
                      </div>
                    </div>
                    <div
                      className={`${
                        user?.role == "clinic" ? "col-span-1" : "col-span-2"
                      }`}
                    >
                      <div className="text-sm">
                        {product.remainingStocks} in stock
                      </div>
                    </div>
                    <div
                      className={`${
                        user?.role == "clinic" ? "col-span-2" : "col-span-2"
                      }`}
                    >
                      <button
                        onClick={() => onAddProduct(product)}
                        className="px-3 py-1 bg-white border border-gray-300 rounded-full text-sm hover:bg-gray-50"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Rows per page:</span>
                  <select
                    value={pagination?.itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="text-sm border rounded p-1"
                  >
                    <option value={2}>2</option>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    Page {pagination?.currentPage} of {pagination?.totalPages}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => onPageChange(pagination?.currentPage - 1)}
                      disabled={pagination?.currentPage === 1}
                      className={`p-1 rounded ${
                        pagination?.currentPage === 1
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => onPageChange(pagination?.currentPage + 1)}
                      disabled={
                        pagination?.currentPage === pagination?.totalPages
                      }
                      className={`p-1 rounded ${
                        pagination?.currentPage === pagination?.totalPages
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Rest of your component remains the same */}
            <h2 className="text-lg font-semibold text-gray-800 mb-4 mt-4">
              Selected Products
            </h2>

            <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-500 mb-2 bg-cyan-50 p-2">
              <div
                className={`${
                  user?.role == "clinic" ? "col-span-3" : "col-span-3"
                }`}
              >
                Product Name
              </div>
              <div
                className={`${
                  user?.role == "clinic" ? "col-span-3" : "col-span-3"
                }`}
              >
                Product Salt
              </div>
              {user?.role == "admin" && <div className="col-span-2">Price</div>}
              <div
                className={`${
                  user?.role == "clinic" ? "col-span-1" : "col-span-2"
                }`}
              >
                Quantity
              </div>
              <div
                className={`${
                  user?.role == "clinic" ? "col-span-2" : "col-span-2"
                }`}
              >
                Actions
              </div>
            </div>

            <div
              className="space-y-2 max-h-[300px] overflow-y-auto"
              style={{
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "thin",
              }}
            >
              {selectedProducts.map((product) => (
                <div
                  key={product.compositeId}
                  className="grid grid-cols-12 gap-6 items-center p-3 rounded-lg"
                >
                  <div
                    className={`${
                      user?.role == "clinic" ? "col-span-3" : "col-span-3"
                    } flex items-center gap-3`}
                  >
                    <img
                      src={product.product_image || medicineIcon}
                      alt={product.product_name}
                      className="w-10 h-10 object-cover rounded"
                    />
                    <div>
                      <div className="font-medium text-gray-800">
                        {product.product_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {product.manufacturer_name}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`${
                      user?.role == "clinic" ? "col-span-3" : "col-span-3"
                    } flex items-center gap-3`}
                  >
                    <div>
                      <div className="font-medium text-gray-800">
                        {product.salt.split(",").map((salt, index) => (
                          <div key={index}>{salt.trim()}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`${
                      user?.role == "clinic" ? "col-span-2" : "col-span-2"
                    }`}
                  >
                    <div className="font-medium">
                      ₹{" "}
                      {(product.finalSP * product?.selectedQuantity).toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-500">
                      ₹ {product?.finalSP?.toFixed(2)} each
                    </div>
                  </div>
                  <div
                    className={`${
                      user?.role == "clinic" ? "col-span-1" : "col-span-2"
                    }`}
                  >
                    <div className="flex items-center gap-2 border-t-2 border-gray-50 border-b-2 bg-cyan-50">
                      <button
                        onClick={() =>
                          product.selectedQuantity > 1 &&
                          onUpdateQuantity(
                            product.compositeId,
                            product.selectedQuantity - 1
                          )
                        }
                        className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center bg-gray-300"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center">
                        {product.selectedQuantity}
                      </span>
                      <button
                        onClick={() =>
                          product.selectedQuantity < product.quantity &&
                          onUpdateQuantity(
                            product.compositeId,
                            product.selectedQuantity + 1
                          )
                        }
                        className="w-6 h-6 rounded-full bg-green-700 text-white flex items-center justify-center"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div
                    className={`${
                      user?.role == "clinic" ? "col-span-2" : "col-span-2"
                    }`}
                  >
                    <button
                      onClick={() => onRemoveProduct(product.compositeId)}
                      className="px-3 py-1 bg-red-100 text-red-600 border border-red-200 rounded-full text-sm hover:bg-red-200"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <hr className="mt-6"/>

        {(
          <div className="mb-4 py-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Doctor Name
            </label>
            <input
              type="text"
              placeholder="Doctor's Name"
              value={doctorName}
              onChange={(e) => setDoctorName(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 placeholder-gray-400 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsSection;
